/**
 * 「历史快照」的**纯逻辑层** —— 零 DOM · 零网络 · 可直测
 *
 * 依据：N 域 PRD **§7.4 `AC-N4.21`**（覆盖前快照**用户可见、可回滚** · 必测）·
 * **`DR-039`**（覆盖前快照载体与回滚口径 · 已拍板生效）·
 * 服务端已落地（批次 `67`：`snapshot.service.ts` + `setting.controller.ts` 三条路由）。
 *
 * ## 🔴 本文件**一行口径都不实现**（这是它与被调方共享层/服务端的分工）
 *
 * | 事实 | 唯一实现处 | 本文件做什么 |
 * |---|---|---|
 * | 原因 → 中文（覆盖前 / 回滚前） | 共享层 `snapshotReasonLabel()` | **只调用**（🔴 不写第二份映射表） |
 * | 体积 → `"1.5 MB"` | 共享层 `formatBytes()` | **只调用** |
 * | 列表排序（最新在前） | 共享层 `sortSnapshots()` | **只调用**（列表可能被乐观更新合并 ⇒ 排序规则必须同一份） |
 * | 保留几份 / 该删哪份 | 服务端（写入事务内） | **不碰** —— 端侧只展示 `keepLimit` |
 * | 回滚的闸门（四道 + 完整性核验） | 服务端（复用 `overwrite` 路径） | **不预判** —— 服务端说不行就照原样报错 |
 *
 * ⇒ 本文件只剩下**三件端侧独有的事**：① 把一行快照拼成"给人看的文本"；
 * ② 决定**二次确认该显示哪几段**（纯 UI 层的破坏性动作确认，`DR-039` D5）；
 * ③ 回滚请求体的形状（只有一个字段）。
 *
 * ## 🔴 为什么"回滚要不要二次确认"能在端侧判定（与恢复刻意不同）
 *
 * `RestoreDialog` 里"要不要前置导出 / 二次输入账本名"**必须**读服务端 `requires` ——
 * 因为那三条闸的判据在服务端（`AC-N4.19` / `AC-N4.20` / `N-D2`），端侧自己写 `mode === 'overwrite'`
 * 就是抄第二份规则。
 *
 * ⚠️ **但回滚不同**：`contract/snapshot.ts` 的 `RollbackSnapshotRequest` **明文**写道 ——
 * 用户确认**不在契约里**（"它是纯 UI 层的二次确认"），且服务端**恒定**会走 `overwrite`
 * （`snapshot.service.ts` 的 `rollback()` 里 `mode: 'overwrite'` 是硬编码的）。
 * ⇒ **"回滚 = 覆盖 = 破坏性"是契约规定的事实，不是端侧的猜测** ⇒ 端侧无条件要给确认。
 * 📌 反过来说：**若哪天回滚不再恒定走 `overwrite`，本文件的判据会静默失真** ——
 * 但那一天必须先改契约文件头（那里是这条判据的出处）。
 */

import {
  formatBytes,
  sortSnapshots,
  snapshotReasonLabel,
  type SnapshotSummaryView,
} from '@jiaying/shared';

/** 列表视图的**最小形状**（🔴 只声明本文件真正读的字段，不重声明整个契约类型） */
export interface SnapshotRowFact {
  readonly id: string;
  readonly createdAt: number;
  readonly reason: string;
  readonly sizeBytes: number;
  readonly createdById: string | null;
}

/** 体积文本 —— 🔴 **转发**共享层 `formatBytes`（不新造换算，铁律 24） */
export function formatSizeText(bytes: number): string {
  return formatBytes(bytes);
}

/**
 * 一行快照的**展示文本**（时刻 · 来源 · 体积）。
 *
 * 🔴 两处都**不是**本文件实现的：来源走共享层 `snapshotReasonLabel`、体积走共享层 `formatBytes`。
 * 本函数只负责**拼**。
 *
 * ## 时刻为什么不在这里格式化
 *
 * `createdAt` 是 **UTC 毫秒**（全库时间约定），转成本地日历日/时刻属于**呈现层**的事
 * （账本时区是账本属性 —— 架构 §4.3）。本文件只做**纯字符串拼接**，
 * 真正的日期格式化由组件用共享层的 `localDayOf()` 完成并**作为入参传进来** ——
 * 这样本函数保持"纯"（同一入参恒得同一结果，与时区无关），spec 也就不必假装时区。
 */
export function snapshotRowText(row: SnapshotRowFact, dayText: string): string {
  return `${dayText} · ${snapshotReasonLabel(row.reason)} · ${formatSizeText(row.sizeBytes)}`;
}

/**
 * 列表按时间倒序（最新在前）—— 🔴 **转发**共享层 `sortSnapshots`。
 *
 * 为什么不在这里直接调：组件拿到列表后可能与"刚回滚时新增的那份"合并（乐观更新），
 * 而那一步也要排序 ⇒ **排序规则只能有一份**（共享层）。本层的存在是"端侧入口统一"，
 * 不是"再实现一遍"。
 */
export function orderedSnapshots(items: readonly SnapshotSummaryView[]): SnapshotSummaryView[] {
  return sortSnapshots(items);
}

/**
 * 二次确认要显示的段落 —— **恒为两段**（影响 + 安全网）。
 *
 * 🔴 抽成常量而不是写在 JSX 里，是为了让 spec 能对**"这两段都在、且没有第三段"**下断言：
 * 只说"会影响什么"而不说"滚错了能回来"⇒ 用户不敢点（功能等于没做）；
 * 只说"能回来"而不说"会影响什么"⇒ 用户以为无害（`DR-039` §4.3 的安全网会被误读成"可撤销"）。
 */
export const ROLLBACK_CONFIRM_PARAGRAPHS = ['impact', 'safety'] as const;
export type RollbackConfirmParagraph = (typeof ROLLBACK_CONFIRM_PARAGRAPHS)[number];

/** 二次确认的段落清单（🔴 目前恒定 —— 若哪天要加"跨版本"那段，判据须来自服务端下发的事实） */
export function rollbackConfirmParagraphs(): readonly RollbackConfirmParagraph[] {
  return ROLLBACK_CONFIRM_PARAGRAPHS;
}

/**
 * 回滚请求体 —— 🔴 **只有 `snapshotId`**。
 *
 * 契约原文（`RollbackSnapshotRequest`）："回滚的目标由快照自身决定（`payload` 里的账本 id），
 * 端侧传不了'回滚到哪本账'这种东西"。⇒ 本函数**不接收**也不猜测任何额外字段 ——
 * 写成一个显式函数是为了让人一眼看出"确实只有这一个字段"。
 */
export function buildRollbackRequest(snapshotId: string): { snapshotId: string } {
  return { snapshotId };
}

/**
 * 把 id 编码后拼进下载路径。
 *
 * 🔴 路由**前缀**不在这里拼（那会与 `endpoints.ts` 里的路由字符串成为两处真相）——
 * 本函数只做**编码**这一件事。⚠️ id 形如 `lsnap_xxx`（`IdPrefix.LedgerSnapshot`）理论上无需编码；
 * 但**由我们自己**把 id 拼进 URL 时一律编码 —— 不靠"我的 id 生成器不会产出怪字符"这个假设。
 */
export function encodeSnapshotId(snapshotId: string): string {
  return encodeURIComponent(snapshotId);
}
